import React, { useState } from "react";
import { Save, Settings2 } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const ProjectForm = () => {
  const [formData, setFormData] = useState({
    projectName: "",
    tagline: "",
    developer: "Hobayer Golondaz Tushar",
    description: "",
    problem: "",
    solution: "",
    users: "",
    business: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
  };

  return (
    <AdminPageShell
      title="System Settings"
      description="Manage Cherrfy project identity and business metadata. (Mock settings)"
      chips={
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black text-slate-800">
          <Settings2 className="h-4 w-4" />
          Project details
        </span>
      }
      actions={
        <>
          <SecondaryButton
            type="button"
            onClick={() =>
              setFormData({
                projectName: "",
                tagline: "",
                developer: "Hobayer Golondaz Tushar",
                description: "",
                problem: "",
                solution: "",
                users: "",
                business: "",
              })
            }
          >
            Reset
          </SecondaryButton>
          <PrimaryButton type="submit" form="project-settings-form">
            <Save className="h-4 w-4" />
            Save
          </PrimaryButton>
        </>
      }
    >
      <form id="project-settings-form" onSubmit={handleSubmit} className="space-y-6">
        <Card className="p-5 md:p-6">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Cherrfy Details</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Basic identity and ownership information.</p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="Project name" name="projectName" value={formData.projectName} onChange={handleChange} />
            <InputField label="Project tagline" name="tagline" value={formData.tagline} onChange={handleChange} />
            <InputField
              label="Developer"
              name="developer"
              value={formData.developer}
              onChange={handleChange}
              disabled
              helpText="Locked for now"
            />
          </div>
        </Card>

        <Card className="p-5 md:p-6">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Product & Business</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Describe the project, users, and business context.</p>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TextAreaField label="Project Description" name="description" value={formData.description} onChange={handleChange} />
            <TextAreaField label="Problem at a glance" name="problem" value={formData.problem} onChange={handleChange} />
            <TextAreaField label="Offered solution" name="solution" value={formData.solution} onChange={handleChange} />
            <TextAreaField label="Targeted Users" name="users" value={formData.users} onChange={handleChange} />
            <div className="lg:col-span-2">
              <TextAreaField label="Business perspective" name="business" value={formData.business} onChange={handleChange} />
            </div>
          </div>
        </Card>
      </form>
    </AdminPageShell>
  );
};

const InputField = ({ label, name, value, onChange, disabled = false, helpText }) => (
  <div>
    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={cx(FieldBase, disabled && "bg-slate-50 text-slate-600")}
    />
    {helpText ? <p className="mt-2 text-xs text-slate-500">{helpText}</p> : null}
  </div>
);

const TextAreaField = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</label>
    <textarea
      name={name}
      rows={4}
      value={value}
      onChange={onChange}
      className={cx(FieldBase, "min-h-[120px] resize-y")}
    />
  </div>
);

export default ProjectForm;
